import axios from 'axios';
import { Gruppe, Spieler, Session, Runde, Punkt, LeaderboardEntry } from './types';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export class ApiService {
  // Gruppen
  static async getGruppen(): Promise<Gruppe[]> {
    const response = await api.get('/gruppen');
    return response.data;
  }

  static async createGruppe(name: string): Promise<Gruppe> {
    const response = await api.post('/gruppen', { name });
    return response.data;
  }

  static async getGruppe(id: number): Promise<Gruppe> {
    const response = await api.get(`/gruppen/${id}`);
    return response.data;
  }

  static async updateGruppe(id: number, name: string): Promise<Gruppe> {
    const response = await api.put(`/gruppen/${id}`, { name });
    return response.data;
  }

  static async deleteGruppe(id: number): Promise<void> {
    await api.delete(`/gruppen/${id}`);
  }

  // Spieler
  static async getSpieler(gruppeId: number): Promise<Spieler[]> {
    const response = await api.get(`/gruppen/${gruppeId}/spieler`);
    return response.data;
  }

  static async createSpieler(gruppeId: number, name: string): Promise<Spieler> {
    const response = await api.post(`/gruppen/${gruppeId}/spieler`, { name });
    return response.data;
  }

  static async updateSpieler(id: number, name: string): Promise<Spieler> {
    const response = await api.put(`/spieler/${id}`, { name });
    return response.data;
  }

  static async deleteSpieler(id: number): Promise<void> {
    await api.delete(`/spieler/${id}`);
  }

  // Sessions
  static async getSessions(gruppeId: number): Promise<Session[]> {
    const response = await api.get(`/gruppen/${gruppeId}/sessions`);
    return response.data;
  }

  static async createSession(gruppeId: number): Promise<Session> {
    const response = await api.post(`/gruppen/${gruppeId}/sessions`, {});
    return response.data;
  }

  static async getSession(id: number): Promise<Session> {
    const response = await api.get(`/sessions/${id}`);
    return response.data;
  }

  static async updateSession(id: number, endedAt?: string): Promise<Session> {
    const response = await api.put(`/sessions/${id}`, { ended_at: endedAt });
    return response.data;
  }

  static async deleteSession(id: number): Promise<void> {
    await api.delete(`/sessions/${id}`);
  }

  // Runden
  static async getRunden(sessionId: number): Promise<Runde[]> {
    const response = await api.get(`/sessions/${sessionId}/runden`);
    return response.data;
  }

  static async createRunde(sessionId: number, nummer: number, cabo_spieler_id?: number): Promise<Runde> {
    const response = await api.post(`/sessions/${sessionId}/runden`, { nummer, cabo_spieler_id });
    return response.data;
  }

  static async updateRunde(id: number, nummer: number, cabo_spieler_id?: number): Promise<Runde> {
    const response = await api.put(`/runden/${id}`, { nummer, cabo_spieler_id });
    return response.data;
  }

  static async deleteRunde(id: number): Promise<void> {
    await api.delete(`/runden/${id}`);
  }

  // Punkte
  static async getPunkte(rundeId: number): Promise<Punkt[]> {
    const response = await api.get(`/runden/${rundeId}/punkte`);
    return response.data;
  }

  static async setPunkte(rundeId: number, punkte: Punkt[]): Promise<Punkt[]> {
    const response = await api.post(`/runden/${rundeId}/punkte`, punkte);
    return response.data;
  }

  // Leaderboard
  static async getLeaderboard(gruppeId: number): Promise<LeaderboardEntry[]> {
    const response = await api.get(`/gruppen/${gruppeId}/leaderboard`);
    return response.data;
  }

  // Backup/Restore
  static async createBackup(): Promise<{ message: string; filename: string; path: string }> {
    const response = await api.post('/backup');
    return response.data;
  }

  static async listBackups(): Promise<Array<{ filename: string; size: number; created_at: string }>> {
    const response = await api.get('/backups');
    return response.data;
  }

  static async downloadBackup(filename: string): Promise<Blob> {
    const response = await api.get(`/backup/${filename}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  static async restoreBackup(filename: string): Promise<{ message: string; filename: string; pre_restore_backup: string }> {
    const formData = new FormData();
    formData.append('filename', filename);
    const response = await api.post('/restore', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  static async deleteBackup(filename: string): Promise<{ message: string; filename: string }> {
    const response = await api.delete(`/backup/${filename}`);
    return response.data;
  }

  static async uploadBackup(file: File): Promise<{ message: string; filename: string; size: number; created_at: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/backup/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
} 